import React, {useRef, useEffect, useState} from "react"
import Leaflet from "leaflet"
import classNames from "classnames"
import {
  Map,
  TileLayer,
  ZoomControl,
  FeatureGroup,
  LayersControl,
  LayerGroup,
  Pane
} from "react-leaflet"
import { EditControl } from "react-leaflet-draw"
import Control from "react-leaflet-control"
import { connect } from "react-redux"
import MeasureControl from "./MeasureControl"
import {selectNewAreaDispatchAction} from "../../state/geoMap/dispatchActions"
import {
  removeLastShapeAction,
  updateMapPositionAction,
} from "../../state/geoMap/actions"
import {
  convertDrawElementIntoShape,
  getCoordinatesFromSearchResultElement,
  getCoordinatesFromSelectedArea,
  isCircleSelectionVisible, isPathStringValid,
  isPolygonSelectionVisible,
} from "../../helpers/shapeHelper"
import {
  panMapToBBoxBoundsCoordinates,
  panMapToCenterCoordinates,
  SELECT_SHAPE_COLOR,
} from "../../helpers/geoMapHelper"
import { MAP_INITIAL_DEFAULT } from "../../state/geoMap/reducer"
import { MapInitialAttributesType } from "../../types/geoMap"
import {
  ShapeType,
} from "../../types/shape"
import {
  ProductImageStacType,
  SearchResultElementType, SearchResultsType,
} from "../../types/product"
import { ConfigurationsType } from "../../types/configurations"
import GeoMapElements from "./GeoMapElements"
import { isSearchResultElementAvailable } from "../../helpers/searchResultsHelper"
import { getCoordinatesFromGeoJSON } from "../../helpers/geoJSONHelper"
import { AllQuicklookPreviewsType } from "../../state/geoMap/types"
import { getQuicklookPreviewsShownInMap } from "../../helpers/quicklookPreviewHelper"
import translations from "../../translations"
import GeoMapLegend from "./GeoMapLegend"
import ManualAOIHelper from "./manual-aoi-helper/ManualAOIHelper"
import {getPathStringBy, hasBeenFilled} from "../../helpers/dynamicFormHelper";
import LocationAOIHelper from "./manual-aoi-helper/LocationAOIHelper"
import {AppParametersType} from "../../types/app";

const { BaseLayer } = LayersControl

interface PropsType {
  configurations: ConfigurationsType,
  formValues: any,
  lastShape: ShapeType,
  width: number,
  searchResultDetails: SearchResultElementType,
  mapInitialAttributes: MapInitialAttributesType,
  selectedProducts: ProductImageStacType[],
  allQuicklookPreviews: AllQuicklookPreviewsType,
  language: string,
  selectedArea: ShapeType,
  selectNewAreaDispatchActionHandler: any,
  removeLastShapeActionHandler: any,
  updateMapPositionActionHandler: any,
  kmlGuideShape: ShapeType,
  appParameters: AppParametersType,
  searchByProducts: boolean,
  searchResults: SearchResultsType
}

const GeoMap = (props: PropsType) => {
  const {
    configurations,
    lastShape,
    mapInitialAttributes,
    selectNewAreaDispatchActionHandler,
    removeLastShapeActionHandler,
    updateMapPositionActionHandler,
    searchResultDetails,
    selectedProducts,
    formValues,
    width,
    allQuicklookPreviews,
    language,
    kmlGuideShape,
    appParameters,
    searchByProducts,
    searchResults
  } = props

  // Keep latest props in a ref.
  const latestProps = useRef(props)
  const mapContainer = useRef<any>(null)
  const zoomButtonsParent = useRef<any>(null)
  const featureGroupRef = useRef<any>(null)

  const [path, setPath] = useState("");

  useEffect(() => {
    latestProps.current = props
    return () => {}
  })

  useEffect(() => {
    if (
      navigator.geolocation &&
      mapInitialAttributes.center[0] === MAP_INITIAL_DEFAULT.center[0] &&
      mapInitialAttributes.center[1] === MAP_INITIAL_DEFAULT.center[1]
    ) {
      navigator.geolocation.getCurrentPosition((p) => {
        const { latitude, longitude } = p.coords
        updateMapPositionActionHandler({
          center: [latitude, longitude],
          zoom: 7,
        })
      })
    }
    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    panMapToCenterCoordinates(
      mapContainer,
      mapInitialAttributes.center,
      mapInitialAttributes.zoom
    )

    return () => {}
  }, [mapInitialAttributes])

  useEffect(() => {
    const allCoordinates: any = []
    setPath(getPathStringBy(lastShape))
    const selectedAreaCoordinates = getCoordinatesFromSelectedArea(lastShape)
    if (selectedAreaCoordinates.length > 0) {
      allCoordinates.push(selectedAreaCoordinates)
    }

    if (allCoordinates && allCoordinates.length) {
      panMapToBBoxBoundsCoordinates(mapContainer, allCoordinates)
    }

    return () => {}
  }, [lastShape])
  console.log("WIDTH")

  useEffect(() => {
    const allCoordinates: any = []
    const selectedAreaCoordinates =
      getCoordinatesFromSelectedArea(kmlGuideShape)
    if (selectedAreaCoordinates.length > 0) {
      allCoordinates.push(selectedAreaCoordinates)
    }

    if (allCoordinates && allCoordinates.length) {
      panMapToBBoxBoundsCoordinates(mapContainer, allCoordinates)
    }

    return () => {}
  }, [kmlGuideShape])

  useEffect(() => {
    const {
      configurations: latestConfigurations,
      selectedProducts: latestSelectedProducts,
      lastShape: latestSelectedArea,
    } = latestProps.current

    const allCoordinates: any = []
    if (
      isSearchResultElementAvailable(
        latestConfigurations,
        appParameters,
        searchResultDetails,
        latestSelectedProducts,
        latestSelectedArea
      )
    ) {
      allCoordinates.push(
        getCoordinatesFromSearchResultElement(
          latestConfigurations,
          appParameters,
          searchResultDetails,
          latestSelectedProducts,
          latestSelectedArea
        )
      )
    }
    if (allCoordinates && allCoordinates.length) {
      panMapToBBoxBoundsCoordinates(mapContainer, allCoordinates)
    }

    return () => {}
  }, [appParameters, searchResultDetails])

  useEffect(() => {
    const {
      configurations: latestConfigurations,
      selectedProducts: latestSelectedProducts,
      searchResultDetails: latestSearchResultDetails,
      lastShape: latestSelectedArea,
      allQuicklookPreviews: latestAllQuicklookPreviews,
    } = latestProps.current
    const allCoordinates: any = []
    if (!!latestSearchResultDetails) {
      allCoordinates.push(
        getCoordinatesFromSearchResultElement(
          latestConfigurations,
          appParameters,
          latestSearchResultDetails,
          latestSelectedProducts,
          latestSelectedArea
        )
      )
    } else {
      if (!!latestSelectedProducts && latestSelectedProducts.length > 0) {
        allCoordinates.push(
          ...latestSelectedProducts.map((product: any) => getCoordinatesFromGeoJSON(product))
        )
      }

      const selectedAreaCoordinates =
        getCoordinatesFromSelectedArea(latestSelectedArea)
      if (selectedAreaCoordinates.length > 0) {
        allCoordinates.push(selectedAreaCoordinates)
      }

      const quicklookPreviewsShown = getQuicklookPreviewsShownInMap(
        latestAllQuicklookPreviews
      )
      if (quicklookPreviewsShown && quicklookPreviewsShown.length > 0) {
        allCoordinates.push(
          ...quicklookPreviewsShown.map((quicklookPreview) => {
            const { upLeft, upRight, bottomLeft, bottomRight } =
              quicklookPreview.corners
            return [upLeft, upRight, bottomLeft, bottomRight]
          })
        )
      }
    }

    if (allCoordinates && allCoordinates.length) {
      panMapToBBoxBoundsCoordinates(mapContainer, allCoordinates)
    }

    // error with measurement
    setTimeout(() => mapContainer.current.leafletElement.invalidateSize(), 400)

    return () => {}
  }, [appParameters, width])

  const zoomResetFunction = () => {
    const initialZoom = 3
    let currentZoom = mapContainer.current.leafletElement._zoom
    if (initialZoom === currentZoom) return
    zoomButtonsParent.current.leafletElement._container
      .querySelector(
        `.leaflet-control-zoom-${initialZoom > currentZoom ? "in" : "out"}`
      )
      .click()
    currentZoom = initialZoom > currentZoom ? currentZoom + 1 : currentZoom - 1

    if (initialZoom !== currentZoom) setTimeout(zoomResetFunction, 0)
  }

  const onCreateHandler = (drawElement) => {
    // remove all previously selected shapes
    Object.keys(featureGroupRef.current.leafletElement._layers)
      .filter((id) => {
        const element = featureGroupRef.current.leafletElement._layers[id]
        return element.options.color === SELECT_SHAPE_COLOR
      })
      .forEach((id) => {
        featureGroupRef.current.leafletElement.removeLayer(id)
      })

    const newShape = convertDrawElementIntoShape(
      configurations,
      drawElement,
      formValues,
      language
    )
    selectNewAreaDispatchActionHandler(
      configurations,
      appParameters,
      "",
      formValues,
      newShape,
      language,
      searchByProducts,
      searchResults
    )
  }

  return (
    <Map
      className={classNames({
        "draw-first-top-control-map": !isPolygonSelectionVisible(formValues),
      })}
      center={MAP_INITIAL_DEFAULT.center}
      zoom={MAP_INITIAL_DEFAULT.zoom}
      maxZoom={17}
      minZoom={3}
      crs={Leaflet.CRS.EPSG3857}
      maxBounds={Leaflet.latLngBounds(
        Leaflet.latLng(-90, -180),
        Leaflet.latLng(90, 180)
      )}
      ref={mapContainer}
      style={{ width }}
      zoomControl={false}
      zoomAnimation={false}
      fadeAnimation={false}
      animate={false}
      noMoveStart
      trackResize
      useFlyTo={false}
      tap={false}
      preferCanvas
    >
      <ZoomControl ref={zoomButtonsParent} position="bottomright" />

      <FeatureGroup ref={featureGroupRef}>
        <GeoMapLegend />
        <Control
          // @ts-ignore
          className="path-importer-control first-top-control"
          position="topright"
        >
          <div className="tools-container">
            <ManualAOIHelper />
          </div>
        </Control>
        <Control
          // @ts-ignore
          className="search-location-control"
          position="topright"
        >
          <div className="tools-container">
            <LocationAOIHelper />
          </div>
        </Control>

        {/* <Control
            // @ts-ignore
            className="download-kml"
            position="topright"
        >
          <div className="tools-container">
            <DownloadAOI path={path} />
          </div>
      </Control> */}

        <EditControl
          position="topright"
          onCreated={onCreateHandler}
          onDeleted={removeLastShapeActionHandler}
          edit={{ remove: false, edit: false }}
          draw={{
            circlemarker: false,
            polyline: false,
            marker: false,
            polygon: isPolygonSelectionVisible(formValues)
              ? {
                showArea: true,
                showLength: true,
                metric: ["km", "m"],
                feet: false,
                nautic: false,
              }
              : false,
            rectangle: false, // @mapitaly disattivato su richiesta (perchè presente bug non trascinamento)
            circle: isCircleSelectionVisible(formValues),
          }}
        />
        <Control
          // @ts-ignore
          className="remove-aoi-control"
          position="topright"
        >
          <div className="tools-container">
            <button
              title={translations("remove-aoi", language)}
              type="button"
              disabled={!lastShape}
              onClick={removeLastShapeActionHandler}
              className={`remove-aoi tool-item remove-btn-defaults${!lastShape ? " leaflet-disabled" : ""}`}
            />
          </div>
        </Control>
        <MeasureControl
          position="topright"
          primaryLengthUnit="meters"
          secondaryLengthUnit="kilometers"
          primaryAreaUnit="sqmeters"
          secondaryAreaUnit="acres"
          activeColor="#67a6fd"
          completedColor="#67a6fd"
        />
        <LayersControl position="topright">
          <BaseLayer checked name={translations("streetsMap", language)}>
            <TileLayer
              className="map-layer-view"
              attribution="Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
            />
          </BaseLayer>
          <BaseLayer name={translations("darkMap", language)}>
            <TileLayer
              className="map-layer-view"
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution={
                "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors &copy; <a href=\"https://carto.com/attributions\">CARTO</a>"
              }
            />
          </BaseLayer>
          <BaseLayer name={translations("topographicMap", language)}>
            <TileLayer
              className="map-layer-view"
              attribution="Tiles &copy; Esri &mdash; Esri, DeLorme, ... , Esri China (Hong Kong), and the GIS User Community"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
            />
          </BaseLayer>
          <BaseLayer name={translations("satelliteMap", language)}>
            <TileLayer
              className="map-layer-view"
              attribution="Tiles &copy; Esri &mdash; Esri, DeLorme, ... , Esri China (Hong Kong), and the GIS User Community"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
          </BaseLayer>
          <BaseLayer name={translations("satelliteMapWithLabels", language)}>
            <LayerGroup>
              <TileLayer
                className="map-layer-view"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution={
                  "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors &copy; <a href=\"https://carto.com/attributions\">CARTO</a>"
                }
              />
              <TileLayer
                className="map-layer-view"
                attribution="Tiles &copy; Esri &mdash; Esri, DeLorme, ... , Esri China (Hong Kong), and the GIS User Community"
                url="https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}.png"
              />
            </LayerGroup>
          </BaseLayer>
        </LayersControl>
        <Control
          // @ts-ignore
          className="reset-zoom-control last-top-control"
          position="topright"
        >
          <div className="tools-container">
            <button
              title={translations("globalMapView", language)}
              type="button"
              onClick={zoomResetFunction}
              className="reset-zoom tool-item remove-btn-defaults"
            />
          </div>
        </Control>
      </FeatureGroup>
      <Pane name="interferometric-stack-pane" style={{ zIndex: 500 }} />

      <GeoMapElements
        configurations={configurations}
        formValues={formValues}
        selectedProducts={selectedProducts}
        searchResultDetails={searchResultDetails}
        lastShape={lastShape}
        kmlGuideShape={kmlGuideShape}
        allQuicklookPreviews={allQuicklookPreviews}
      />
    </Map>
  )
}

const mapStateToProps = (state) => {
  const formValues = {
    routine: state.application.routine,
    missions: state.application.multiMissionSelected ? state.application.multiMissionSelected : state.application.missionSelected,
    language: state.application.appParameters.language,
    ...state.application.searchFiltersSelected,
  }

  return {
    lastShape: state.geoMap.lastShape,
    kmlGuideShape: state.geoMap.kmlGuideShape,
    allQuicklookPreviews: state.geoMap.allQuicklookPreviews,
    mapInitialAttributes: state.geoMap.mapInitialAttributes,
    searchResultDetails: state.application.searchResultDetails,
    selectedProducts: state.product.selectedProducts,
    configurations: state.application.configurations,
    language: state.application.appParameters.language,
    formValues,
    appParameters: state.application.appParameters,
    searchByProducts: state.geoMap.searchByProducts,
    searchResults: state.product.searchResults
  }
}

const mapDispatchToProps = (dispatch) => ({
  removeLastShapeActionHandler: () => dispatch(removeLastShapeAction()),
  updateMapPositionActionHandler: (e) => dispatch(updateMapPositionAction(e)),
  selectNewAreaDispatchActionHandler: (
    configurations,
    appParameters: AppParametersType,
    scopeFields,
    formValues,
    newShape,
    language,
    searchByProducts,
    searchResults
  ) => dispatch(
    selectNewAreaDispatchAction(
      configurations,
      appParameters,
      scopeFields,
      formValues,
      newShape,
      language,
      searchByProducts,
      searchResults
    )
  ),
})

export default connect(mapStateToProps, mapDispatchToProps)(GeoMap)
